import { Link } from 'react-router-dom';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import type { EducationCartItem } from '../../model/education-cart.type';
import type { CheckoutOrderer } from '../../model/education-checkout.type';
import {
  formatEducationPrice,
  formatSessionDateTime,
} from '../../lib/education-format';

interface CheckoutSummaryProps {
  items: EducationCartItem[];
  isBusiness: boolean;
  orderer: CheckoutOrderer;
  /** 기업 소속회사 표시값 — 계정 값이 비면 구성원 조회 응답의 회사명으로 채운다. */
  companyName: string;
  attendeeCount: (sessionId: string) => number;
  total: number;
  missingAttendees: boolean;
  ordererIncomplete: boolean;
  submitting: boolean;
  submitDisabled: boolean;
}

/** 우측 접수 요약 — 회차별 인원, 총 결제 금액, 제출 버튼. */
export function CheckoutSummary({
  items,
  isBusiness,
  orderer,
  companyName,
  attendeeCount,
  total,
  missingAttendees,
  ordererIncomplete,
  submitting,
  submitDisabled,
}: CheckoutSummaryProps) {
  return (
    <aside className="lg:sticky lg:top-24" aria-label="접수 요약">
      <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.02]">
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 dark:border-white/10">
          <h2 className="font-semibold text-gray-950 dark:text-neutral-100">
            접수 요약
          </h2>
          <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-neutral-400">
            {items.length}개 회차
          </span>
        </div>

        {/* 기업은 신청자 입력칸이 없는 대신, 무엇으로 접수되는지 여기서 확인한다. */}
        {isBusiness && (
          <dl className="border-b border-gray-200 py-4 text-sm dark:border-white/10">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-xs text-gray-500 dark:text-neutral-400">
                신청자
              </dt>
              <dd className="min-w-0 truncate font-medium text-gray-900 dark:text-neutral-100">
                {orderer.ordererName || '-'}
              </dd>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-xs text-gray-500 dark:text-neutral-400">
                소속회사
              </dt>
              <dd className="min-w-0 truncate font-medium text-gray-900 dark:text-neutral-100">
                {companyName || '-'}
              </dd>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-xs text-gray-500 dark:text-neutral-400">
                연락처
              </dt>
              <dd className="min-w-0 truncate font-medium tabular-nums text-gray-900 dark:text-neutral-100">
                {orderer.ordererPhone || '-'}
              </dd>
            </div>
          </dl>
        )}

        <ul className="divide-y divide-gray-100 dark:divide-white/10">
          {items.map((item) => (
            <li key={item.sessionId} className="py-4">
              <p className="text-xs font-medium text-brand-700 dark:text-brand-300">
                {item.courseTitle}
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-neutral-100">
                {item.sessionTitle}
              </p>
              <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-500 dark:text-neutral-400">
                <span>{formatSessionDateTime(item.startAt)}</span>
                <span className="shrink-0 tabular-nums">
                  {attendeeCount(item.sessionId)}명
                </span>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex items-end justify-between border-t border-gray-200 pt-4 dark:border-white/10">
          <p className="text-sm font-medium text-gray-600 dark:text-neutral-300">
            총 결제 금액
          </p>
          <p className="text-2xl font-bold tabular-nums text-gray-950 dark:text-neutral-100">
            {formatEducationPrice(total)}
          </p>
        </div>
        {missingAttendees && (
          <p className="mt-3 text-xs font-medium leading-5 text-red-600 dark:text-red-400">
            모든 회차에 수강자를 한 명 이상 추가해 주세요.
          </p>
        )}
        {ordererIncomplete && (
          <p className="mt-3 text-xs font-medium leading-5 text-red-600 dark:text-red-400">
            계정에 이름·연락처·이메일이 모두 있어야 접수할 수 있습니다.{' '}
            <Link
              to={PATHS.mypageTab('profile')}
              className="underline underline-offset-4"
            >
              내 정보에서 채워 주세요
            </Link>
            .
          </p>
        )}
        <button
          type="submit"
          disabled={submitDisabled}
          className="mt-5 h-12 w-full rounded-md bg-brand-600 px-5 font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-neutral-700"
        >
          {submitting
            ? '처리 중…'
            : total === 0
              ? '접수 완료하기'
              : '결제하기'}
        </button>

        {/* 청약철회 제한 기준은 결제 전에 명확히 보여야 효력이 있다 (전자상거래법 제17조 제6항, 이용약관 제23조). */}
        <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-neutral-400">
          결제 완료 전에는 좌석이 확정되지 않습니다. 취소 시 환불 기준: 교육 시작
          2일 전까지 전액, 1일 전 50%, 당일 이후 환불 불가(회사 사정으로 취소된
          경우 전액). 취소·환불은 고객센터로 문의해 주세요.
        </p>
      </div>
    </aside>
  );
}
